import { Button } from "@sellar-io/components";
import { useParentNavigation } from "../../../hooks/useParentNavigation";
import {
  STOCK_OVERVIEW,
  STOCK_OVERVIEW_PRODUCT_DETAILS,
} from "../../../constants/routes";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { useSupplierProduct } from "../../../hooks/api/v2/product/useSupplierProduct";
import { Icon, ProfilePicture, StockIndicator } from "../../../components";
import { MaxOrderQuantityBadge } from "../../../components/badge/MaxOrderQuantityBadge";
import { AvailableFromBadge } from "../../../components/badge/AvailableFromBadge";
import StockManagementDetailsDialog from "../../product/list/StockManagementDetailsDialog/StockManagementDetailsDialog";
import ItemDetailsDialog from "../../product/list/ProductItemModal/ItemDetailsDialog";
import { useState } from "react";
import { getProductDisplayNameByKey } from "@sellar-io/common";
import { ProductDeleteDialog } from "../components/ProductDeleteDialog";

const getFormattedContainerType = (containerType: string | null) => {
  if (containerType === "badge") {
    return "Badge";
  }

  if (containerType) {
    return getProductDisplayNameByKey(containerType);
  }

  return null;
};

export const StockOverviewProduct = () => {
  const productId = useParamAsNumber(
    STOCK_OVERVIEW_PRODUCT_DETAILS,
    "productId",
  );

  const [
    showStockManagementDetailsDialog,
    setShowStockManagementDetailsDialog,
  ] = useState(false);
  const [showProductDetailsDialog, setShowProductDetailsDialog] =
    useState(false);
  const [showProductDeleteDialog, setShowProductDeleteDialog] = useState(false);

  const navigateToParent = useParentNavigation({
    fallback: STOCK_OVERVIEW,
  });

  const productQuery = useSupplierProduct(productId);

  const productItem = productQuery.data;
  const [stockManagementProduct, setStockManagementProduct] =
    useState<typeof productItem>();

  const volumeAndUnit =
    productItem?.volume && productItem.unit
      ? `${productItem.volume}${productItem.unit}`
      : null;

  const containerType = getFormattedContainerType(
    productItem?.type === "badge"
      ? "badge"
      : (productItem?.containerType ?? null),
  );

  const weight = productItem?.weight ? `${productItem.weight} kg` : null;
  const v = productItem?.vegan ?? productItem?.Parent?.vegan ?? false;
  const gf =
    productItem?.glutenFree ?? productItem?.Parent?.glutenFree ?? false;
  const lf =
    productItem?.lactoseFree ?? productItem?.Parent?.lactoseFree ?? false;

  const dietaryList = [
    ...(v ? ["VG"] : []),
    ...(gf ? ["GF"] : []),
    ...(lf ? ["LF"] : []),
  ].join(", ");

  const parentProductInactive = !productItem?.Parent?.active;

  return (
    <div className="flex h-full flex-col bg-white">
      <NavigationBar
        title={productItem?.Parent?.name ?? ""}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={navigateToParent}
      />

      <div className="px-4 py-8">
        {/* item details section*/}

        <div
          className="flex cursor-pointer flex-col gap-4"
          onClick={() => setShowProductDetailsDialog(true)}
        >
          {/* title and action button */}

          <div className="flex items-center justify-between">
            <div className="font-semibold text-black">Item details</div>
            <Icon icon={"PENCIL"} color="text-black" />
          </div>

          {/* product image ands name */}

          <div className="flex items-center gap-2 rounded-lg border border-zinc-300 p-4">
            <ProfilePicture
              logoUrl={productItem?.Parent?.heroImageUrl}
              size={"medium"}
            />
            <div>
              <div className="text-sm font-semibold text-zinc-800">
                {productItem?.Parent?.name}
              </div>
              <div className="text-sm font-light">
                {parentProductInactive && (
                  <span className="text-red-500">
                    Inactive <span className="text-zinc-500"> • </span>
                  </span>
                )}
                <span className="text-zinc-500">
                  SKU: {productItem?.sku ?? "-"}
                </span>
              </div>
            </div>
          </div>

          {/* product details */}

          <div className="grid grid-cols-2 gap-y-2">
            <div className="text-sm font-semibold text-zinc-800">Container</div>
            <div className="text-sm text-zinc-500">
              {containerType ? containerType : "-"}
            </div>
            <div className="text-sm font-semibold text-zinc-800">
              Volume/Unit
            </div>
            <div className="text-sm text-zinc-500">
              {volumeAndUnit ? volumeAndUnit : "-"}
            </div>
            <div className="text-sm font-semibold text-zinc-800">
              Pack quantity
            </div>
            <div className="text-sm text-zinc-500">
              {productItem?.packQuantity ?? "-"}
            </div>
            <div className="text-sm font-semibold text-zinc-800">Weight</div>
            <div className="text-sm text-zinc-500">{weight ?? "-"}</div>
            <div className="text-sm font-semibold text-zinc-800">Dietary</div>
            <div className="text-sm text-zinc-500">{dietaryList}</div>
          </div>
        </div>

        <div className="my-4 border-b border-zinc-300" />

        {/* stock management section*/}

        <div
          className="flex cursor-pointer flex-col gap-4"
          onClick={() => {
            setStockManagementProduct(productItem);
            setShowStockManagementDetailsDialog(true);
          }}
        >
          {/* title and action button */}
          <div className="flex items-center justify-between">
            <div className="font-semibold text-black">Stock management</div>
            <Icon icon={"PENCIL"} color="text-black" />
          </div>

          {/* stock management highlight pills */}

          <div className="flex items-center gap-1">
            <StockIndicator
              stock={productItem?.availableStock ?? 0}
              type={"short"}
            />

            {!!productItem?.availableFrom && (
              <AvailableFromBadge availableFrom={productItem.availableFrom} />
            )}

            {!!productItem?.maxOrderQuantity && (
              <MaxOrderQuantityBadge
                fullSize={false}
                maxQuantity={productItem.maxOrderQuantity}
              />
            )}

            {!productItem?.active && (
              <Icon icon="EYE_OFF" color="text-black" size={0.9} />
            )}
          </div>

          {/* stock level table */}

          <div
            className={
              "overflow-x-auto rounded-lg border border-solid border-gray-200"
            }
          >
            <table className={"w-full table-auto border-spacing-0"}>
              <thead className={"text-left"}>
                <tr>
                  {!!productItem?.incomingStock && (
                    <th className="border-r border-b border-gray-200 bg-zinc-50 p-2 text-xs">
                      {" "}
                      Incoming
                    </th>
                  )}

                  <th className="border-r border-b border-gray-200 bg-zinc-50 p-2 text-xs">
                    Total (on hand)
                  </th>
                  <th className="border-r border-b border-gray-200 bg-zinc-50 p-2 text-xs">
                    Committed
                  </th>
                  <th className="border-b bg-zinc-50 p-2 text-xs">Available</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  {!!productItem?.incomingStock && (
                    <td className="border-r border-gray-200 bg-white p-2">
                      {productItem.incomingStock}
                    </td>
                  )}

                  <td className="border-r border-gray-200 bg-white p-2 text-sm">
                    {productItem?.stock}
                  </td>
                  <td className="border-r border-gray-200 bg-white p-2 text-sm">
                    {productItem?.committedStock ?? 0}
                  </td>
                  <td className="bg-white p-2 text-sm">
                    {productItem?.availableStock ?? 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-auto mb-8 flex w-full justify-center">
        <Button
          color={"inverse-red"}
          onClick={() => setShowProductDeleteDialog(true)}
        >
          <div className="flex items-center gap-2">
            <Icon icon="DELETE" color="red" />
            <div className="text-red-500">DELETE ITEM</div>
          </div>
        </Button>
      </div>

      {/* delete item dialog */}

      <ProductDeleteDialog
        productName={productItem?.name?.split("•")[1] ?? "product"}
        productId={productItem?.id}
        onDismiss={() => {
          setShowProductDeleteDialog(false);
        }}
        active={showProductDeleteDialog}
        onDelete={navigateToParent}
      />

      {/* edit item details dialog */}

      <ItemDetailsDialog
        active={showProductDetailsDialog}
        onDismiss={() => setShowProductDetailsDialog(false)}
        product={productItem}
      />

      {/* edit stock management dialog */}

      <StockManagementDetailsDialog
        active={showStockManagementDetailsDialog}
        onDismiss={() => setShowStockManagementDetailsDialog(false)}
        afterLeave={() => setStockManagementProduct(undefined)}
        product={stockManagementProduct}
      />
    </div>
  );
};
